import React, { useState, useEffect, useRef, useMemo } from 'react';
import { ChevronDown, ChevronUp, Coins, RefreshCw, Users, X, LockKeyhole, UnlockKeyhole } from 'lucide-react';
import Info from './investmentFunds/Info';
import { useToast } from '../../../../../components/ui/toast/ToastContext';
import { camelizeKeys } from '../../../../../utils/camelizeKeys';
import BalancingBadge from '../../../../../components/ui/badges/BalancingBadge';
import CustomCheckbox from '../../../../../components/ui/checkboxes/CustomCheckbox';
import { fetchWithCsrf } from '../../../../../utils/fetchWithCsrf';
import SortableHeader from '../../../../../components/ui/table/SortableHeader';
import PercentageInput from '../../../../../components/ui/inputs/PercentageInput';

const BASE_GRID = '30px 1fr 1.5fr 1fr 1fr 1fr 140px';
const BASE_GRID_POSITIONS = '1.5fr 1fr 1fr 140px';

const normalizeSortableText = (value) => {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
};

const getSortableCertificateValue = (cert, field) => {
  switch (field) {
    case 'name':
      return normalizeSortableText(cert.name);

    case 'certificateType':
      return normalizeSortableText(cert.certificateType);

    case 'taxRegime':
      return normalizeSortableText(cert.taxRegime);

    case 'status':
      return normalizeSortableText(cert.tStatus);

    case 'amount':
      return Number(cert.amount) || 0;

    default: {
      const value = cert[field];

      if (typeof value === 'number') {
        return value;
      }

      return normalizeSortableText(value);
    }
  }
};

const sortCertificates = (certificates = [], sortBy, sortOrder) => {
  const direction = sortOrder === 'desc' ? -1 : 1;

  return [...certificates]
    .map((cert, index) => ({ cert, index }))
    .sort((a, b) => {
      const valueA = getSortableCertificateValue(a.cert, sortBy);
      const valueB = getSortableCertificateValue(b.cert, sortBy);

      let result = 0;

      if (typeof valueA === 'number' && typeof valueB === 'number') {
        result = valueA - valueB;
      } else {
        result = String(valueA).localeCompare(String(valueB), 'pt-BR', {
          numeric: true,
          sensitivity: 'base',
        });
      }

      if (result === 0) {
        return a.index - b.index;
      }

      return result * direction;
    })
    .map(({ cert }) => cert);
};

const getSortableFundValue = (position, field) => {
  switch (field) {
    case 'fund':
      return normalizeSortableText(position.product && position.product.displayName);

    case 'amount':
      return Number(position.amount) || 0;

    case 'idealAmount':
      return Number(position.idealAmount) || 0;

    case 'balancing':
      return Number(position.balancing) || 0;

    default: {
      const value = position[field];

      if (typeof value === 'number') {
        return value;
      }

      return normalizeSortableText(value);
    }
  }
};

const sortFunds = (funds = [], sortBy, sortOrder) => {
  const direction = sortOrder === 'desc' ? -1 : 1;

  return [...funds]
    .map((position, index) => ({ position, index }))
    .sort((a, b) => {
      const valueA = getSortableFundValue(a.position, sortBy);
      const valueB = getSortableFundValue(b.position, sortBy);

      let result = 0;

      if (typeof valueA === 'number' && typeof valueB === 'number') {
        result = valueA - valueB;
      } else {
        result = String(valueA).localeCompare(String(valueB), 'pt-BR', {
          numeric: true,
          sensitivity: 'base',
        });
      }

      if (result === 0) {
        return a.index - b.index;
      }

      return result * direction;
    })
    .map(({ position }) => position);
};

const getPensionFundLockId = (item) => {
  return String(item.product?.id || item.id);
};

const isPensionFundLocked = (item) => {
  return Boolean(item.product?.locked || item.locked || item.isLocked || item.fundLocked || item.productLocked);
};

const getInitialLockedPensionFundIds = (certificates = []) => {
  return certificates.flatMap((certificate) => {
    return (certificate.positions || []).flatMap((position) => {
      const ids = [];

      if (isPensionFundLocked(position)) {
        ids.push(getPensionFundLockId(position));
      }

      (position.complementaryPositions || []).forEach((comp) => {
        if (isPensionFundLocked(comp)) {
          ids.push(getPensionFundLockId(comp));
        }
      });

      return ids;
    });
  });
};

const getAllCertificates = (data = {}) => {
  return [...(data.pensionPgblCertificates || []), ...(data.pensionVgblCertificates || [])];
};

const formatCurrencyPlain = (value) => {
  if (value == null) return 'R$ 0,00';
  const [intPart, decPart] = Number(value).toFixed(2).replace('.', ',').split(',');
  const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `R$ ${formattedInt},${decPart}`;
};

const formatPercentage = (value) => {
  if (value == null) return '0,0%';
  return `${Number(value).toFixed(1).replace('.', ',')}%`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const [year, month, day] = dateStr.split('-');
  return `${day}/${month}/${year}`;
};

const Title = ({ count }) => (
  <div className="bg-blueLight flex flex-row justify-between items-center p-4 rounded-xl">
    <div className="flex items-center gap-3">
      <Coins className="text-tealBase" size={20} strokeWidth={1.75} />
      <span className="text-blueDarker text-xl font-semibold">
        Previdência <span className="font-normal">({count} ativos)</span>
      </span>
    </div>

    <div className="flex items-center gap-5 text-blueDarker/70">
      <div className="flex items-center gap-2">
        <span
          className="flex-none block rounded"
          style={{ backgroundColor: '#55A32A', width: '12px', height: '12px' }}
        />
        <span className="text-base text-blueDarker">Cliente + Modelo</span>
      </div>

      <div className="flex items-center gap-2">
        <span
          className="flex-none block rounded"
          style={{ backgroundColor: '#622CAC', width: '12px', height: '12px' }}
        />
        <span className="text-base text-blueDarker">Apenas Modelo</span>
      </div>

      <div className="flex items-center gap-2">
        <span
          className="flex-none block rounded"
          style={{ backgroundColor: '#B37133', width: '12px', height: '12px' }}
        />
        <span className="text-base text-blueDarker">Apenas Cliente</span>
      </div>
    </div>
  </div>
);

const NotaBadge = ({ score, tone }) => {
  let color = '';

  if (tone === 'success') {
    color = 'bg-tealDeep';
  } else if (tone === 'danger') {
    color = 'bg-redBase';
  } else if (tone === 'neutral') {
    color = 'bg-yellowDark';
  } else if (tone === 'none') {
    color = 'bg-grayDark';
  }

  return (
    <span
      style={{ borderRadius: '10px', paddingTop: '2px', paddingBottom: '2px' }}
      className="relative text-blueDarker inline-flex bg-white text-base font-medium px-3 flex-row items-center gap-2 transition-opacity"
    >
      <span
        className={`flex-none block rounded ${color}`}
        style={{
          width: '12px',
          height: '12px',
        }}
      />

      <span className="font-semibold">
        Nota: {score}
        <span className="font-normal">/100</span>
      </span>
    </span>
  );
};

const LockIconButton = ({ locked, disabled = false, onClick }) => {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={locked ? 'Fundo trancado' : 'Fundo destrancado'}
      className={`flex-shrink-0 flex items-center gap-1.5 rounded-full transition-opacity ${
        disabled ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-80'
      }`}
    >
      <span
        className={`flex items-center justify-center transition-colors ${
          locked ? 'text-redSoft' : 'text-graySemiDark'
        }`}
      >
        {locked ? <LockKeyhole size={14} strokeWidth={2.5} /> : <UnlockKeyhole size={14} strokeWidth={2.3} />}
      </span>

      <span
        className={`relative inline-flex h-4 w-8 flex-shrink-0 rounded-full transition-colors duration-200 ease-in-out ${
          locked ? 'bg-redExtraLight' : 'bg-bluePale'
        }`}
      >
        <span
          className={`absolute left-0 top-1/2 h-5 w-5 rounded-full border shadow-sm transition-transform duration-200 ease-in-out ${
            locked ? 'bg-redSoft border-redSoft' : 'bg-white border-bluePale'
          }`}
          style={{
            transform: locked ? 'translate(13px, -50%)' : 'translate(0px, -50%)',
          }}
        />
      </span>
    </button>
  );
};

const fundColor = (situation) => {
  switch (situation) {
    case 'wc':
      return 'text-greenBright';
    case 'w':
      return 'text-purpleBright';
    case 'c':
      return 'text-orangeBright';
    default:
      return 'text-blueDarker';
  }
};

const BeneficiariesModal = ({ apiClientId, certificate, beneficiaries, onClose, onSaved }) => {
  const { addToast } = useToast();
  const allocations = new Map(
    (certificate.beneficiaries || []).map((allocation) => [String(allocation.beneficiaryId), allocation])
  );
  const [rows, setRows] = useState(() =>
    (beneficiaries || []).map((beneficiary) => {
      const allocation = allocations.get(String(beneficiary.id));

      return {
        ...beneficiary,
        selected: Boolean(allocation),
        participation: allocation?.participation || '',
      };
    })
  );
  const [saving, setSaving] = useState(false);

  const toggleBeneficiary = (beneficiaryId) => {
    setRows((currentRows) =>
      currentRows.map((row) => (row.id === beneficiaryId ? { ...row, selected: !row.selected } : row))
    );
  };

  const updateParticipation = (beneficiaryId, participation) => {
    setRows((currentRows) => currentRows.map((row) => (row.id === beneficiaryId ? { ...row, participation } : row)));
  };

  const selectedRows = rows.filter((row) => row.selected);
  const totalParticipation = selectedRows.reduce((total, row) => total + (Number(row.participation) || 0), 0);

  const handleSave = async () => {
    const invalidBeneficiary = selectedRows.find((row) => {
      const participation = Number(row.participation);
      return !Number.isFinite(participation) || participation <= 0 || participation > 100;
    });

    if (invalidBeneficiary) {
      addToast({
        type: 'error',
        message: `Informe um percentual entre 0 e 100 para ${invalidBeneficiary.name}.`,
      });
      return;
    }

    setSaving(true);

    try {
      const response = await fetchWithCsrf(
        `/app/analyst/client/${apiClientId}/dashboard/pension_funds/${certificate.id}/beneficiaries`,
        {
          method: 'PUT',
          body: JSON.stringify({
            beneficiaries: selectedRows.map((row) => ({
              beneficiary_id: row.id,
              participation: row.participation,
            })),
          }),
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
        }
      );
      const responseData = camelizeKeys(await response.json().catch(() => ({})));

      if (!response.ok) {
        throw new Error(responseData.message || 'Erro ao atualizar beneficiários.');
      }

      onSaved(certificate.id, responseData.beneficiaries || []);
      addToast({ type: 'success', message: responseData.message || 'Beneficiários atualizados com sucesso.' });
      onClose();
    } catch (error) {
      addToast({ type: 'error', message: error.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={saving ? undefined : onClose} />

      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl mx-4 overflow-hidden">
        <div className="flex items-center justify-between px-7 pt-6 pb-5">
          <div className="flex items-center gap-3">
            <Users size={25} className="text-tealMedium" strokeWidth={1.5} />
            <h2 className="text-2xl font-semibold text-blueDarker">Gerenciar Beneficiários</h2>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-tealExtraLight transition-colors text-tealMedium hover:text-tealDark disabled:opacity-40"
          >
            <X size={21} strokeWidth={1.7} />
          </button>
        </div>

        <div className="px-6 pb-4 max-h-screen-60 overflow-y-auto">
          {rows.length === 0 ? (
            <p className="rounded-xl bg-blueLight text-sm text-blueDarker/50 text-center py-8">
              Nenhum beneficiário cadastrado no perfil do cliente.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <div style={{ minWidth: '670px' }}>
                <div
                  className="grid gap-5 px-4 pb-3 text-base font-semibold text-blueDarker"
                  style={{ gridTemplateColumns: '30px minmax(190px, 2fr) 1fr 1fr 100px' }}
                >
                  <div />
                  <div>Nome Completo</div>
                  <div>Parentesco</div>
                  <div>Nascimento</div>
                  <div>Percentual</div>
                </div>

                <div className="flex flex-col gap-2">
                  {rows.map((beneficiary, index) => (
                    <div
                      key={beneficiary.id}
                      className={`grid gap-5 items-center px-4 py-4 text-base text-blueDarker rounded-xl ${
                        index % 2 === 0 ? 'bg-blueLight' : 'bg-white'
                      }`}
                      style={{ gridTemplateColumns: '30px minmax(190px, 2fr) 1fr 1fr 100px' }}
                    >
                      <CustomCheckbox
                        checked={beneficiary.selected}
                        onChange={() => toggleBeneficiary(beneficiary.id)}
                      />
                      <div className="flex min-w-0 items-center gap-2">
                        <span className="font-semibold truncate">{beneficiary.name}</span>
                      </div>
                      <span className="text-blueDarker/80 truncate">{beneficiary.kinship}</span>
                      <span className="text-blueDarker/80">{formatDate(beneficiary.birthDate)}</span>

                      <PercentageInput
                        name={`beneficiaryParticipation${beneficiary.id}`}
                        disabled={!beneficiary.selected}
                        required={beneficiary.selected}
                        value={beneficiary.participation}
                        onChange={(event) => updateParticipation(beneficiary.id, event.target.value)}
                        py="py-2"
                        inputClassName="px-4 text-sm text-blueDarker disabled:bg-grayLight disabled:text-blueDarker/30"
                      />
                    </div>
                  ))}

                  <span className="flex justify-end px-4 pt-3 text-base text-blueDarker">
                    <strong className="mr-1">Total:</strong> {formatPercentage(totalParticipation)}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 px-7 pb-6 pt-1">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="rounded-xl border border-bluePale bg-white px-6 py-2.5 text-sm font-bold text-blueDarker transition-colors hover:bg-blueLight disabled:opacity-50"
          >
            CANCELAR
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="bg-tealMedium hover:opacity-90 transition-opacity text-white font-bold rounded-xl px-6 py-2.5 text-sm border-r-2 border-b-2 border-tealBase disabled:opacity-50"
          >
            {saving ? 'SALVANDO...' : 'SALVAR BENEFICIÁRIOS'}
          </button>
        </div>
      </div>
    </div>
  );
};

const PositionRow = ({ position, lockedFundIds, lockingFundIds, onToggleFundLock }) => {
  const [isExpanded, setIsExpanded] = useState(true);

  const hasComplements = position.complementaryPositions && position.complementaryPositions?.length > 0;
  const totalAmount = Number(position.amountWithComplements || position.amount) || 0;
  const totalPct = Number(position.amountWithComplementsPercentage || position.amountPercentage) || 0;

  const positionLockId = getPensionFundLockId(position);
  const positionLocked = lockedFundIds.includes(positionLockId);
  const positionLocking = lockingFundIds.includes(positionLockId);

  const percentageTextColor = (value) => {
    if (value > 0) return 'text-yellowMedium';
    if (value < 0) return 'text-redSoft';
    return 'text-tealDark';
  };

  return (
    <div className="bg-white rounded-xl">
      <div
        className="grid items-center gap-3 px-3 py-4 text-sm text-blueDarker overflow-x-auto"
        style={{ gridTemplateColumns: BASE_GRID_POSITIONS }}
      >
        <div
          className={`font-semibold flex items-center gap-3 min-w-0 ${fundColor(position.situation)} ${
            hasComplements ? 'cursor-pointer hover:opacity-80' : ''
          }`}
          title={position.product.displayName}
          onClick={() => hasComplements && setIsExpanded((v) => !v)}
        >
          <LockIconButton
            locked={positionLocked}
            disabled={positionLocking}
            onClick={(e) => {
              e.stopPropagation();
              onToggleFundLock(position);
            }}
          />

          <span className="truncate">{position.product.displayName}</span>

          {hasComplements && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setIsExpanded((v) => !v);
              }}
              className="flex-shrink-0 w-5 h-5 rounded-full bg-white/70 border border-current/20 flex items-center justify-center hover:bg-white transition-colors"
            >
              {isExpanded ? (
                <ChevronUp size={12} className="text-blueMedium transition-transform duration-300" strokeWidth={2} />
              ) : (
                <ChevronDown size={12} className="text-blueMedium transition-transform duration-300" strokeWidth={2} />
              )}
            </button>
          )}
        </div>

        <div className="truncate">
          {formatCurrencyPlain(position.amount)}
          <span className={`font-semibold ${percentageTextColor(position.amountPercentage)} ml-1`}>
            ({formatPercentage(position.amountPercentage)})
          </span>
        </div>

        <div className="truncate">
          {formatCurrencyPlain(position.idealAmount)}
          <span className="font-semibold ml-1">({formatPercentage(position.idealPercentage)})</span>
        </div>

        <BalancingBadge value={position.balancing} />
      </div>

      {hasComplements && (
        <div
          className={`grid transition-all duration-300 ease-in-out ${isExpanded ? 'opacity-100' : 'opacity-0'}`}
          style={{ gridTemplateRows: isExpanded ? '1fr' : '0fr' }}
        >
          <div className="overflow-hidden">
            <hr className="border-bluePale" />

            <div className="pb-2">
              {position.complementaryPositions?.map((comp) => {
                const compLockId = getPensionFundLockId(comp);
                const compLocked = lockedFundIds.includes(compLockId);
                const compLocking = lockingFundIds.includes(compLockId);

                return (
                  <div
                    key={comp.id}
                    className="grid gap-3 items-center px-3 py-2 text-sm rounded-lg"
                    style={{ gridTemplateColumns: BASE_GRID_POSITIONS }}
                  >
                    <div className={`font-semibold truncate flex items-center gap-3 ${fundColor(comp.situation)}`}>
                      <LockIconButton
                        locked={compLocked}
                        disabled={compLocking}
                        onClick={(e) => {
                          e.stopPropagation();
                          onToggleFundLock(comp);
                        }}
                      />

                      <span className="truncate">{comp.product.displayName}</span>
                    </div>

                    <div>
                      {formatCurrencyPlain(comp.amount)}
                      <span className={`font-semibold ${percentageTextColor(comp.amountPercentage)} ml-1`}>
                        ({formatPercentage(comp.amountPercentage)})
                      </span>
                    </div>

                    <div>
                      {formatCurrencyPlain(comp.idealAmount)}
                      <span className="font-semibold ml-1">({formatPercentage(comp.idealPercentage)})</span>
                    </div>

                    <BalancingBadge value={comp.balancing} />
                  </div>
                );
              })}

              <div className="grid gap-3 items-center px-3 py-2" style={{ gridTemplateColumns: BASE_GRID_POSITIONS }}>
                <div className="pl-2 text-sm font-semibold uppercase text-right">Total:</div>

                <div className="text-sm">
                  {formatCurrencyPlain(totalAmount)}
                  <span className="font-semibold ml-1">({formatPercentage(totalPct)})</span>
                </div>

                <div />
                <div />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const CertificateFundsTable = ({ funds, lockedFundIds, lockingFundIds, onToggleFundLock }) => {
  const [sortBy, setSortBy] = useState('fund');
  const [sortOrder, setSortOrder] = useState('asc');

  const sortedFunds = useMemo(() => {
    return sortFunds(funds || [], sortBy, sortOrder);
  }, [funds, sortBy, sortOrder]);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="grid transition-all duration-300 ease-in-out opacity-100" style={{ gridTemplateRows: '1fr' }}>
        <div className="overflow-hidden">
          <div className="px-5 pt-1 pb-5">
            <div
              className="grid items-center gap-3 text-blueDarker text-base font-semibold px-3 pt-7 pb-2"
              style={{ gridTemplateColumns: BASE_GRID_POSITIONS }}
            >
              <SortableHeader field="fund" label="Fundo" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />

              <SortableHeader
                field="amount"
                label="Valor Investido"
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSort={handleSort}
              />

              <SortableHeader
                field="idealAmount"
                label="Valor Ideal"
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSort={handleSort}
              />

              <SortableHeader
                field="balancing"
                label="Balanceamento"
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSort={handleSort}
              />
            </div>

            <div className="space-y-3">
              {sortedFunds?.map((position) => (
                <PositionRow
                  key={position.id}
                  position={position}
                  lockedFundIds={lockedFundIds}
                  lockingFundIds={lockingFundIds}
                  onToggleFundLock={onToggleFundLock}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const CertificateRow = ({
  apiClientId,
  cert,
  isSelected,
  onToggle,
  beneficiaries,
  onBeneficiariesSaved,
  lockedFundIds,
  lockingFundIds,
  onToggleFundLock,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [showBeneficiaries, setShowBeneficiaries] = useState(false);

  return (
    <>
      {showBeneficiaries && (
        <BeneficiariesModal
          apiClientId={apiClientId}
          certificate={cert}
          beneficiaries={beneficiaries}
          onSaved={onBeneficiariesSaved}
          onClose={() => setShowBeneficiaries(false)}
        />
      )}

      <div className="bg-blueLight rounded-xl">
        <div
          className="grid items-center gap-4 px-5 py-4 text-base text-blueDarker overflow-x-auto"
          style={{ gridTemplateColumns: BASE_GRID }}
        >
          <CustomCheckbox checked={isSelected} onChange={() => onToggle(cert.id)} />

          <div className="font-semibold truncate">{cert.name || '—'}</div>
          <div className="truncate">{cert.certificateType || '—'}</div>
          <div className="truncate">{cert.taxRegime || '—'}</div>
          <div className="truncate">{cert.tStatus || '—'}</div>
          <div className="truncate">{formatCurrencyPlain(cert.amount)}</div>

          <div className="flex items-center gap-2 justify-between">
            <button
              type="button"
              disabled={cert.persisted === false}
              title={cert.persisted === false ? 'Beneficiários indisponíveis para certificados não cadastrados.' : ''}
              onClick={() => cert.persisted !== false && setShowBeneficiaries(true)}
              className="bg-tealMedium hover:opacity-90 transition-opacity text-white font-semibold rounded-xl px-2 pt-3 pb-2 text-xs border-r-2 border-b-2 border-tealBase disabled:bg-grayMedium disabled:border-graySemiDark disabled:cursor-not-allowed disabled:opacity-60"
            >
              BENEFICIÁRIOS
            </button>

            <button
              type="button"
              onClick={() => setIsExpanded((v) => !v)}
              className="text-blueDarker/60 hover:text-blueDarker transition-colors flex-shrink-0"
            >
              {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </button>
          </div>
        </div>

        <div
          className={`grid transition-all duration-300 ease-in-out ${isExpanded ? 'opacity-100' : 'opacity-0'}`}
          style={{ gridTemplateRows: isExpanded ? '1fr' : '0fr' }}
        >
          <div className="overflow-hidden">
            <hr className="border-bluePale" />

            <CertificateFundsTable
              funds={cert.positions}
              lockedFundIds={lockedFundIds}
              lockingFundIds={lockingFundIds}
              onToggleFundLock={onToggleFundLock}
            />
          </div>
        </div>
      </div>
    </>
  );
};

const PensionTypeAccordion = ({
  apiClientId,
  title,
  investedAmount,
  investedPercentage,
  nota,
  notaTone,
  certificates,
  selectedIds,
  onToggle,
  onToggleAll,
  defaultOpen = true,
  beneficiaries,
  onBeneficiariesSaved,
  lockedFundIds,
  lockingFundIds,
  onToggleFundLock,
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');

  const sortedCertificates = useMemo(() => {
    return sortCertificates(certificates || [], sortBy, sortOrder);
  }, [certificates, sortBy, sortOrder]);

  const visibleIds = certificates?.map((c) => c.id) || [];
  const allSelected = visibleIds?.length > 0 && visibleIds.every((id) => selectedIds.includes(id));
  const someSelected = visibleIds?.some((id) => selectedIds.includes(id)) && !allSelected;

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <div
        className="flex items-center justify-between bg-tealMedium text-white font-normal px-4 py-3 rounded-xl cursor-pointer select-none hover:bg-tealDark transition-colors"
        onClick={() => setIsOpen((v) => !v)}
      >
        <span className="text-base font-semibold">{title}</span>

        <div className="flex items-center gap-4">
          <span className="text-base font-medium">
            <span className="font-bold">Investido: </span>
            {formatCurrencyPlain(investedAmount)} ({formatPercentage(investedPercentage)})
          </span>

          <NotaBadge score={nota} tone={notaTone} />

          {isOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </div>
      </div>

      <div
        className={`grid transition-all duration-300 ease-in-out ${isOpen ? 'opacity-100' : 'opacity-0'}`}
        style={{ gridTemplateRows: isOpen ? '1fr' : '0fr' }}
      >
        <div className="overflow-hidden">
          <div className="px-2 pt-1 pb-2">
            <div
              className="grid items-center gap-4 text-blueDarker text-base font-semibold px-5 py-2"
              style={{ gridTemplateColumns: BASE_GRID }}
            >
              <CustomCheckbox
                checked={allSelected}
                indeterminate={someSelected}
                onChange={() => onToggleAll(visibleIds)}
              />

              <SortableHeader
                field="name"
                label="Certificado"
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSort={handleSort}
              />

              <SortableHeader
                field="certificateType"
                label="Tipo"
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSort={handleSort}
              />

              <SortableHeader
                field="taxRegime"
                label="Regime Tributário"
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSort={handleSort}
              />

              <SortableHeader field="status" label="Status" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />

              <SortableHeader
                field="amount"
                label="Valor Total (R$)"
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSort={handleSort}
              />

              <div />
            </div>

            <div className="space-y-5 mt-1">
              {sortedCertificates?.map((cert) => (
                <CertificateRow
                  key={cert.id}
                  apiClientId={apiClientId}
                  cert={cert}
                  isSelected={selectedIds.includes(cert.id)}
                  onToggle={onToggle}
                  beneficiaries={beneficiaries}
                  onBeneficiariesSaved={onBeneficiariesSaved}
                  lockedFundIds={lockedFundIds}
                  lockingFundIds={lockingFundIds}
                  onToggleFundLock={onToggleFundLock}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Pension = ({ apiClientId, onPositionUpdated }) => {
  const { addToast } = useToast();

  const [data, setData] = useState({
    totalInPension: 0,
    totalInPensionPercentage: 0,
    totalPendingSettlementsInPension: 0,
    investedAmountPgbl: '0.0',
    investedAmountVgbl: '0.0',
    investedAmountPgblPercentage: '0.0',
    investedAmountVgblPercentage: '0.0',
    pgblBalancingScore: 0,
    vgblBalancingScore: 0,
    pensionPgblCertificates: [],
    pensionVgblCertificates: [],
    beneficiaries: [],
  });

  const [selectedIds, setSelectedIds] = useState([]);
  const [lockedFundIds, setLockedFundIds] = useState([]);
  const [lockingFundIds, setLockingFundIds] = useState([]);

  const lockingFundIdsRef = useRef(new Set());

  const pgblPositions = data.pensionPgblCertificates || [];
  const vgblPositions = data.pensionVgblCertificates || [];
  const totalCount = pgblPositions?.length + vgblPositions?.length;

  const groups = [
    {
      id: 'pgbl',
      title: 'PGBL',
      investedAmount: data.investedAmountPgbl,
      investedPercentage: data.investedAmountPgblPercentage,
      nota: data.pgblBalancingScore,
      notaTone: data.scoreTonePgbl,
      certificates: pgblPositions,
    },
    {
      id: 'vgbl',
      title: 'VGBL',
      investedAmount: data.investedAmountVgbl,
      investedPercentage: data.investedAmountVgblPercentage,
      nota: data.vgblBalancingScore,
      notaTone: data.scoreToneVgbl,
      certificates: vgblPositions,
    },
  ];

  const toggleOne = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const toggleGroup = (ids) => {
    const allIn = ids.every((id) => selectedIds.includes(id));

    if (allIn) {
      setSelectedIds((prev) => prev.filter((id) => !ids.includes(id)));
    } else {
      setSelectedIds((prev) => [...new Set([...prev, ...ids])]);
    }
  };

  const fetchPensions = async () => {
    await fetch(`/app/analyst/client/${apiClientId}/dashboard/pension_funds`)
      .then((res) => {
        return res.json();
      })
      .then((responseData) => {
        const parsedData = camelizeKeys(responseData) || {};

        setData(parsedData);

        setSelectedIds([
          ...new Set([
            ...((parsedData?.pensionPgblCertificates || []).map((cert) => cert.id) || []),
            ...((parsedData?.pensionVgblCertificates || []).map((cert) => cert.id) || []),
          ]),
        ]);

        setLockedFundIds(getInitialLockedPensionFundIds(getAllCertificates(parsedData)));
      })
      .catch((error) => {
        console.log('error: ', error);

        addToast({
          type: 'error',
          message: 'Erro ao carregar dados de previdência',
        });
      });
  };

  const handleToggleFundLock = async (item) => {
    const id = getPensionFundLockId(item);

    if (lockingFundIdsRef.current.has(id)) {
      return;
    }

    const currentlyLocked = lockedFundIds.includes(id);
    const nextLocked = !currentlyLocked;

    lockingFundIdsRef.current.add(id);
    setLockingFundIds((prev) => [...new Set([...prev, id])]);

    setLockedFundIds((prev) => {
      if (nextLocked) {
        return [...new Set([...prev, id])];
      }

      return prev.filter((lockedId) => lockedId !== id);
    });

    try {
      const res = await fetchWithCsrf(
        `/app/analyst/client/${apiClientId}/dashboard/pension_funds/${item.product.id}/lock`,
        {
          method: 'PATCH',
          body: JSON.stringify({
            locked: nextLocked,
          }),
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
        }
      );

      if (!res.ok) {
        const json = await res.json().catch(() => ({}));
        throw new Error(json.message || 'Erro ao trancar/destrancar fundo de previdência');
      }

      addToast({
        type: 'success',
        message: nextLocked ? 'Fundo trancado com sucesso.' : 'Fundo destrancado com sucesso.',
      });

      fetchPensions();

      if (onPositionUpdated) {
        onPositionUpdated();
      }
    } catch (err) {
      setLockedFundIds((prev) => {
        if (currentlyLocked) {
          return [...new Set([...prev, id])];
        }

        return prev.filter((lockedId) => lockedId !== id);
      });

      addToast({
        type: 'error',
        message: err.message || 'Erro ao atualizar trava do fundo de previdência',
      });
    } finally {
      lockingFundIdsRef.current.delete(id);
      setLockingFundIds((prev) => prev.filter((lockingId) => lockingId !== id));
    }
  };

  const handleBeneficiariesSaved = (certificateId, beneficiaries) => {
    const updateCertificates = (certificates = []) =>
      certificates.map((certificate) =>
        certificate.id === certificateId ? { ...certificate, beneficiaries } : certificate
      );

    setData((currentData) => ({
      ...currentData,
      pensionPgblCertificates: updateCertificates(currentData.pensionPgblCertificates),
      pensionVgblCertificates: updateCertificates(currentData.pensionVgblCertificates),
    }));
  };

  useEffect(() => {
    fetchPensions();
  }, []);

  return (
    <div className="bg-white rounded-custom20 p-4 h-full flex flex-col gap-5">
      <Title count={totalCount} />

      <div className="grid grid-cols-2 gap-4">
        <Info
          title="Total em Previdências"
          value={data.totalInPension || 0}
          percentage={data.totalInPensionPercentage || 0}
          circleClassName="bg-blueWhisper text-tealBase"
        />

        <Info
          title="Previdências em Trânsito"
          value={data.totalPendingSettlementsInPension || 0}
          icon={<RefreshCw size={32} strokeWidth={1.5} />}
          circleClassName="bg-white text-blueHighlight"
        />
      </div>

      <div className="flex flex-col gap-4">
        {groups?.map((group) => (
          <PensionTypeAccordion
            key={group.id}
            apiClientId={apiClientId}
            title={group.title}
            investedAmount={group.investedAmount}
            investedPercentage={group.investedPercentage}
            nota={group.nota}
            notaTone={group.notaTone}
            certificates={group.certificates}
            selectedIds={selectedIds}
            onToggle={toggleOne}
            onToggleAll={toggleGroup}
            beneficiaries={data.beneficiaries}
            onBeneficiariesSaved={handleBeneficiariesSaved}
            lockedFundIds={lockedFundIds}
            lockingFundIds={lockingFundIds}
            onToggleFundLock={handleToggleFundLock}
          />
        ))}
      </div>
    </div>
  );
};

export default Pension;
